import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  View,
  Text,
  ScrollView,
  TextInput,
  Pressable,
  Platform,
  FlatList,
  useWindowDimensions,
  Alert,
  ActivityIndicator,
  Modal,
  TouchableOpacity,
  StyleSheet,
  TouchableWithoutFeedback,
} from 'react-native';
import Svg, { Path, Line } from 'react-native-svg';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../theme';
import { RootState } from '../../store';
import { FontFamily } from '../../constants/fonts';
import { AdminColors } from '../Admin/constants/adminColors';
import { hasDeliveryRPMFullAccess } from './harborRoles';
import PTLanguageToggle from '../PerfectTruck/PTLanguageToggle';
import HarborDataTable from './HarborDataTable';
import HaulbackRecordModal from './HaulbackRecordModal';
import BarcodeScannerSimple from '../../components/BarcodeScannerSimple';
import { useToast } from '../../components/LhToastContainer';
import { badge as baseBadge, es as baseEs, mob as baseMob, s as baseS } from './OneWayScreen.styles';
import { fetchRecentHaulbackSubmissions, validateHaulbackSscc, fetchRoadblockCustomers, parseError, HaulbackSubmissionsParams, OrderInfo, CustomerOption } from '../../api/deliveryRpmApi';
import { resolveReasonLabel } from './deliveryRpmReasonCodeUtils';
import { useDarkStyles, useDarkColor } from '../../theme/useDarkStyles';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface HaulbackRecord {
  id: string;
  customer: string;
  cof: string;
  sscc: string;
  localRoute: string;
  date: string; // ISO YYYY-MM-DD
  status: 'Rescheduled' | 'Return to Stock';
  reasonCode: string;
  caseQuantity: number;
  driver: string;
  salesRep: string;
  picker: string;
}

/** An SSCC that has been validated and staged for multi-submit */
interface StagedSscc {
  id: string;
  sscc: string;
  orderInfo: OrderInfo;
  proceedWhenNotScheduled: boolean;
}

type SortDirection = 'asc' | 'desc';
type QuickFilter = 'all' | 'today' | 'last7' | 'rescheduled' | 'return-to-stock';
type EntryMode = 'sscc' | 'customer';

type CustomerSelection = {
  cof: string;
  name: string;
  sscc?: string;
  ssccList?: string[];
};

// Maps UI column keys → backend sortBy field names
const SORT_KEY_MAP: Record<string, string> = {
  sscc: 'sscc',
  localRoute: 'localRoute',
  reasonCode: 'reason',
  caseQuantity: 'caseQuantity',
  driver: 'driver',
  salesRep: 'salesRep',
  picker: 'picker',
  customer: 'customer',
  status: 'rescheduleStatus',
  date: 'deliveryDate',
};

interface OneWayScreenProps {
  roleName?: string | null;
  onNavigate?: (screen: string, params?: Record<string, any>) => void;
  /** Success message from a Haulback submission, shown as a toast once and then cleared. */
  toastMessage?: string | null;
  onToastShown?: () => void;
}

// ─── Icons ───────────────────────────────────────────────────────────────────

const ArrowUpIcon = ({ color = '#005CBC' }: { color?: string }) => (
  <Svg width={10} height={10} viewBox="0 0 24 24" fill="none">
    <Path d="M12 5l-7 7h14z" fill={color} />
  </Svg>
);

const ArrowDownIcon = ({ color = '#005CBC' }: { color?: string }) => (
  <Svg width={10} height={10} viewBox="0 0 24 24" fill="none">
    <Path d="M12 19l7-7H5z" fill={color} />
  </Svg>
);

const GoArrowIcon = ({ ready }: { ready: boolean }) => (
  <Svg width={18} height={18} viewBox="0 0 24 24" fill="none">
    <Path d="M8 5l8 7-8 7" stroke={ready ? '#fff' : '#BDBDBD'} strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" />
  </Svg>
);

const PlusIcon = ({ ready }: { ready: boolean }) => (
  <Svg width={16} height={16} viewBox="0 0 24 24" fill="none">
    <Path d="M12 5v14M5 12h14" stroke={ready ? '#fff' : '#BDBDBD'} strokeWidth={2.5} strokeLinecap="round" />
  </Svg>
);

const ScanIcon = ({ size = 24, color = '#005CBC' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path
      d="M4 8V5.5A1.5 1.5 0 0 1 5.5 4H8M16 4h2.5A1.5 1.5 0 0 1 20 5.5V8M20 16v2.5a1.5 1.5 0 0 1-1.5 1.5H16M8 20H5.5A1.5 1.5 0 0 1 4 18.5V16"
      stroke={color}
      strokeWidth={2}
      strokeLinecap="round"
    />
    <Line x1="8" y1="9" x2="8" y2="15" stroke={color} strokeWidth={2} strokeLinecap="round" />
    <Line x1="11" y1="9" x2="11" y2="15" stroke={color} strokeWidth={2} strokeLinecap="round" />
    <Line x1="14" y1="9" x2="14" y2="15" stroke={color} strokeWidth={2} strokeLinecap="round" />
    <Line x1="16.5" y1="9" x2="16.5" y2="15" stroke={color} strokeWidth={2} strokeLinecap="round" />
  </Svg>
);

const EyeIcon = () => {
  const dc = useDarkColor();
  return (
    <Svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke={dc("#005CBC")} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <Path d="M2 12s3.5-6.5 10-6.5S22 12 22 12s-3.5 6.5-10 6.5S2 12 2 12Z" />
      <Path d="M12 9.4a2.6 2.6 0 1 0 0 5.2 2.6 2.6 0 0 0 0-5.2Z" />
    </Svg>
  );
};

const PlusTooltip: React.FC<{
  visible: boolean;
  position: { x: number; y: number } | null;
  text: string;
}> = ({ visible, position, text }) => {
  const dc = useDarkColor();
  if (Platform.OS !== 'web' || !visible || !position || !text) return null;
  const win: any = typeof globalThis !== 'undefined' ? (globalThis as any) : {};
  if (!win.document) return null;

  const overlay = (
    <View
      style={{
        position: 'fixed' as any,
        left: position.x + 12,
        top: position.y - 8,
        maxWidth: 240,
        zIndex: 2147483647,
        pointerEvents: 'none' as any,
      }}
    >
      <View
        style={{
          paddingHorizontal: 10,
          paddingVertical: 6,
          borderRadius: 6,
          backgroundColor: dc('#1C1C1C', 'bg'),
        }}
      >
        <Text style={{ color: '#FFFFFF', fontSize: 12, fontWeight: '400', lineHeight: 16 }}>
          {text}
        </Text>
      </View>
    </View>
  );

  return createPortal(overlay, win.document.body);
};

// ─── Status badge ────────────────────────────────────────────────────────────

const StatusBadge = ({ status, t }: { status: HaulbackRecord['status']; t: (key: string) => string }) => {
  const badge = useDarkStyles(baseBadge);
  return (
    <View style={[badge.wrap, status === 'Rescheduled' ? badge.green : badge.red]}>
      <Text style={[badge.text, status === 'Rescheduled' ? badge.greenText : badge.redText]}>
        {t(`one_way.status_badges.${status === 'Rescheduled' ? 'rescheduled' : 'return_to_stock'}`)}
      </Text>
    </View>
  );
};

// ─── Main screen ─────────────────────────────────────────────────────────────

const PAGE_SIZE = 100;
const SSCC_MAX_LEN = 20;

// Returns page indices (0-based) to display, inserting '...' for gaps
const getPageNumbers = (current: number, total: number): (number | '...')[] => {
  if (total <= 5) return Array.from({ length: total }, (_, i) => i);
  const pages: (number | '...')[] = [0];
  if (current > 2) pages.push('...');
  for (let i = Math.max(1, current - 1); i <= Math.min(total - 2, current + 1); i++) pages.push(i);
  if (current < total - 3) pages.push('...');
  pages.push(total - 1);
  return pages;
};

const formatDate = (iso: string) => {
  const [y, m, d] = iso.split('-');
  return `${m}/${d}/${y}`;
};

const todayIso = () => new Date().toISOString().slice(0, 10);

const createStagedSsccId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const normalizeCustomerName = (value?: string) => (value ?? '').trim();

const areSameCustomer = (items: StagedSscc[]) => {
  const firstCustomer = normalizeCustomerName(items[0]?.orderInfo.customer);
  return !!firstCustomer && items.every(item => normalizeCustomerName(item.orderInfo.customer) === firstCustomer);
};

const OneWayScreen: React.FC<OneWayScreenProps> = ({ roleName, onNavigate, toastMessage, onToastShown }) => {
  const dc = useDarkColor();
  const s = useDarkStyles(baseS);
  const mob = useDarkStyles(baseMob);
  const { t } = useTranslation('harbor');
  const isDarkTheme = useTheme().resolvedTheme === 'dark';
  const { showToast } = useToast();
  const { width } = useWindowDimensions();
  const isMobile = width < 768;

  const isFullAccess = hasDeliveryRPMFullAccess(roleName);
  const haulbackRcMap = useSelector((state: RootState) => state.deliveryRpm.haulbackRcMap);
  const selectedLocation = useSelector((state: RootState) => state.appData?.selectedLocation);
  const currentSiteName = useSelector((state: RootState) => state.appData?.selectedLocation?.name);

  // Entry mode toggle: SSCC (default) or Customer
  const [entryMode, setEntryMode] = useState<EntryMode>('sscc');

  // SSCC state
  const [sscc, setSscc] = useState('');
  const [ssccError, setSsccError] = useState('');
  const ssccInputRef = useRef<TextInput>(null);

  // Multi-SSCC staged list
  const [stagedSsccs, setStagedSsccs] = useState<StagedSscc[]>([]);
  const [plusTooltipPosition, setPlusTooltipPosition] = useState<{ x: number; y: number } | null>(null);

  // Same-customer error modal
  const [sameCustomerModal, setSameCustomerModal] = useState(false);

  // Validation loading state
  const [validating, setValidating] = useState(false);
  const [barcodeScannerVisible, setBarcodeScannerVisible] = useState(false);
  const [cameraDenied, setCameraDenied] = useState(false);

  // ── Customer state ─────────────────────────────────────────────────────────
  const [customerText, setCustomerText] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerSelection | null>(null);
  const [customerError, setCustomerError] = useState('');
  const [customerValidating, setCustomerValidating] = useState(false);
  const [customerDropOpen, setCustomerDropOpen] = useState(false);
  const [customerOptions, setCustomerOptions] = useState<CustomerOption[]>([]);
  const [customerEmptyMsg, setCustomerEmptyMsg] = useState<string | null>(null);
  const [customerLoading, setCustomerLoading] = useState(false);
  const customerDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const customerSelectingRef = useRef(false);
  // Anchors the mobile Modal dropdown directly below the input (measured in window coords)
  const customerInputWrapRef = useRef<View>(null);
  const [customerDropPos, setCustomerDropPos] = useState({ top: 0, left: 0, width: 0 });
  const measureCustomerDropdown = () => {
    customerInputWrapRef.current?.measureInWindow((x: number, y: number, w: number, h: number) => {
      setCustomerDropPos({ top: y + h + 4, left: x, width: w });
    });
  };

  // Shared "not scheduled" modal — used by both SSCC and Customer paths
  const [notScheduledModal, setNotScheduledModal] = useState(false);
  const pendingSsccRef = useRef<string>('');
  /** When true, the pending SSCC is for the add-to-list flow, not the submit flow */
  const pendingIsAddRef = useRef<boolean>(false);
  const [pendingCustomer, setPendingCustomer] = useState<CustomerSelection | null>(null);
  const [proceedValidating, setProceedValidating] = useState(false);

  // Table state
  const [quickFilter, setQuickFilter] = useState<QuickFilter>('all');
  const [globalSearch, setGlobalSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [columnFilters, setColumnFilters] = useState<Record<string, string[]>>({});
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [totalElements, setTotalElements] = useState(0);

  // Server-side sort state
  const [sortBy, setSortBy] = useState<string | undefined>(undefined);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  // Date range filter state
  const [dateFrom, setDateFrom] = useState<Date | null>(null);
  const [dateTo, setDateTo] = useState<Date | null>(null);

  // Page data
  const [allData, setAllData] = useState<HaulbackRecord[]>([]);
  const [loadingData, setLoadingData] = useState(true);

  // Record detail modal state
  const [selectedRecordId, setSelectedRecordId] = useState<string | null>(null);

  const toLocalDateStr = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const loadSubmissions = useCallback(async () => {
    setLoadingData(true);
    try {
      const params: HaulbackSubmissionsParams = {
        page: currentPage,
        pageSize,
        sortBy,
        sortDirection,
        search: debouncedSearch.trim() || undefined,
        fromDate: dateFrom ? toLocalDateStr(dateFrom) : undefined,
        toDate: dateTo ? toLocalDateStr(dateTo) : undefined,
      };

      const colKeyToApiParam: Record<string, keyof HaulbackSubmissionsParams> = {
        localRoute: 'localRoute',
        driver: 'driver',
        salesRep: 'salesRep',
        picker: 'picker',
        customer: 'customer',
        status: 'rescheduleStatus',
        reasonCode: 'reasonCodeId',
        caseQuantity: 'caseQuantity',
      };
      const labelToRcId = Object.fromEntries(
        Object.entries(haulbackRcMap).map(([id, label]) => [label, Number(id)]),
      );
      for (const [col, apiKey] of Object.entries(colKeyToApiParam)) {
        const selected = columnFilters[col];
        if (selected && selected.length > 0) {
          if (apiKey === 'caseQuantity') {
            (params as any)[apiKey] = selected.map(Number);
          } else if (apiKey === 'reasonCodeId') {
            (params as any)[apiKey] = selected.map(label => labelToRcId[label]).filter(id => !isNaN(id));
          } else {
            (params as any)[apiKey] = selected;
          }
        }
      }

      const result = await fetchRecentHaulbackSubmissions(params);
      const mapped: HaulbackRecord[] = result.data.map(sub => ({
        id: String(sub.id),
        customer: sub.customerName ?? sub.customer,
        cof: '',
        sscc: sub.sscc,
        localRoute: sub.localRoute,
        date: sub.deliveryDate,
        status: sub.rescheduleStatus === 'Return To Stock' ? 'Return to Stock' : 'Rescheduled',
        reasonCode: resolveReasonLabel(sub.reasonCodeId, haulbackRcMap),
        caseQuantity: sub.caseQuantity,
        driver: sub.driverName ?? sub.driver,
        salesRep: sub.salesRep ?? '',
        picker: sub.picker ?? '',
      }));
      setAllData(mapped);
      setTotalElements(result.pagination?.totalElements ?? mapped.length);
    } catch {
      // Non-fatal: keep empty list
    } finally {
      setLoadingData(false);
    }
  }, [haulbackRcMap, currentPage, pageSize, sortBy, sortDirection, debouncedSearch, columnFilters, dateFrom, dateTo]);

  useEffect(() => { loadSubmissions(); }, [loadSubmissions, selectedLocation?.name]);

  // ── Show success toast for Haulback submissions after navigating back ────────
  useEffect(() => {
    if (!toastMessage) return;
    showToast({
      header: '',
      subtext: toastMessage,
      variant: 'success',
      position: 'top',
      duration: 4000,
    });
    onToastShown?.();
    loadSubmissions();
  }, [toastMessage, onToastShown, showToast, loadSubmissions]);

  // ── SSCC handlers ─────────────────────────────────────────────────────────

  const handleSsccChange = (val: string) => {
    const numeric = val.replace(/\D/g, '');
    setSscc(numeric);
    // Dismiss the camera-denied hint as soon as the user starts typing manually
    if (numeric.length > 0) {
      setCameraDenied(false);
    }
    if (val !== numeric && val.length > 0) {
      setSsccError(t('one_way.error_sscc_digits_only'));
    } else {
      setSsccError('');
    }
  };

  const handleSsccBlur = () => {
    if (sscc.length > 0 && sscc.length !== SSCC_MAX_LEN) {
      setSsccError(t('one_way.error_sscc_length'));
    }
  };

  const runValidation = useCallback(async (value: string, proceed = false, addToList = false) => {
    if (value.length !== SSCC_MAX_LEN) {
      setSsccError(t('one_way.error_sscc_length'));
      return;
    }
    setSsccError('');
    setValidating(true);
    try {
      const result = await validateHaulbackSscc({
        sscc: value,
        proceedWhenNotScheduled: proceed,
      });

      if (result.errorType === 'INVALID_LENGTH') {
        setSsccError(result.inlineError ?? t('one_way.error_sscc_length'));
        return;
      }

      if (result.errorType === 'DUPLICATE_HAULBACK') {
        // Prefer the translated message from i18n instead of any API-provided English inlineError
        setSsccError(t('one_way.error_duplicate_haulback'));
        return;
      }

      if (result.errorType === 'ORDER_DETAILS_UNAVAILABLE') {
        setSsccError(result.inlineError ?? t('one_way.error_order_unavailable'));
        return;
      }

      if (!result.valid && result.requiresProceedConfirmation && result.canProceedWithOverride && !proceed) {
        pendingSsccRef.current = value;
        pendingIsAddRef.current = addToList;
        setNotScheduledModal(true);
        return;
      }

      if (!result.valid) {
        setSsccError(result.inlineError ?? result.bannerError ?? result.modalMessage ?? t('one_way.error_validate_sscc'));
        return;
      }

      if (addToList) {
        // ── Multi-submit: stage this SSCC ────────────────────────────────────
        const newOrderInfo = result.orderInfo ?? null;

        // Validate same-customer constraint when there are already staged SSCCs
        if (stagedSsccs.length > 0 && newOrderInfo) {
          const existingCustomer = stagedSsccs[0].orderInfo.customer;
          if (newOrderInfo.customer !== existingCustomer) {
            setSscc('');
            setSsccError('');
            setSameCustomerModal(true);
            return;
          }
        }

        if (newOrderInfo) {
          setStagedSsccs(prev => [...prev, {
            id: createStagedSsccId(),
            sscc: value,
            orderInfo: newOrderInfo,
            proceedWhenNotScheduled: proceed,
          }]);
          setSscc('');
          setSsccError('');
        } else {
          // orderInfo missing — fall through to regular single submission
          onNavigate?.('Delivery RPM Submit', { sscc: value, proceedWhenNotScheduled: proceed });
        }
      } else {
        // ── Single / final submit: navigate to form ──────────────────────────
        if (stagedSsccs.length > 0) {
          const newOrderInfo = result.orderInfo ?? null;
          const stagedProceedWhenNotScheduled = stagedSsccs.some(st => st.proceedWhenNotScheduled);
          const effectiveProceedWhenNotScheduled = proceed || stagedProceedWhenNotScheduled;
          const allSsccs: StagedSscc[] = newOrderInfo
            ? [...stagedSsccs, {
                id: createStagedSsccId(),
                sscc: value,
                orderInfo: newOrderInfo,
                proceedWhenNotScheduled: proceed,
              }]
            : stagedSsccs;

          if (!areSameCustomer(allSsccs)) {
            setSscc('');
            setSsccError('');
            setSameCustomerModal(true);
            return;
          }

          onNavigate?.('Delivery RPM Submit', {
            sscc: allSsccs[0].sscc,
            proceedWhenNotScheduled: effectiveProceedWhenNotScheduled,
            additionalSsccs: allSsccs.map(st => ({ sscc: st.sscc, orderInfo: st.orderInfo })),
          });
        } else {
          onNavigate?.('Delivery RPM Submit', { sscc: value, proceedWhenNotScheduled: proceed });
        }
      }
    } catch (err) {
      setSsccError(parseError(err));
    } finally {
      setValidating(false);
    }
  }, [onNavigate, t, stagedSsccs]);

  const handleSsccSubmit = () => {
    if (sscc.length === SSCC_MAX_LEN) {
      runValidation(sscc, false, false);
      return;
    }

    if (sscc.length === 0 && stagedSsccs.length > 0) {
      const stagedProceedWhenNotScheduled = stagedSsccs.some(st => st.proceedWhenNotScheduled);
      onNavigate?.('Delivery RPM Submit', {
        sscc: stagedSsccs[0].sscc,
        proceedWhenNotScheduled: stagedProceedWhenNotScheduled,
        additionalSsccs: stagedSsccs.map(st => ({ sscc: st.sscc, orderInfo: st.orderInfo })),
      });
    }
  };

  const handleAddToList = () => {
    if (sscc.length === SSCC_MAX_LEN && !validating) {
      runValidation(sscc, false, true);
    }
  };

  const handleRemoveStagedSscc = (id: string) => {
    setStagedSsccs(prev => prev.filter(st => st.id !== id));
  };

  const handleScan = () => {
    if (Platform.OS === 'web') return;
    setCameraDenied(false);
    setBarcodeScannerVisible(true);
  };

  const handleBarcodeScanSuccess = (scannedValue: string) => {
    setBarcodeScannerVisible(false);
    setSscc(scannedValue);
  };

  // Modal actions
  const handleNotScheduledBack = () => {
    setNotScheduledModal(false);
    if (pendingCustomer) {
      setPendingCustomer(null);
    } else {
      pendingSsccRef.current = '';
      pendingIsAddRef.current = false;
    }
  };

  const handleNotScheduledProceed = async () => {
    const isCustomerPath = !!pendingCustomer;
    if (isCustomerPath) {
      const customer = pendingCustomer!;
      const customerSscc = customer.sscc;
      setProceedValidating(true);
      try {
        if (customerSscc) {
          // Validate SSCC with proceed flag
          const result = await validateHaulbackSscc({ sscc: customerSscc, proceedWhenNotScheduled: true });
          if (!result.valid) {
            setNotScheduledModal(false);
            setPendingCustomer(null);
            setCustomerError(result.inlineError || result.bannerError || result.modalMessage || t('one_way.error_validate_sscc'));
            return;
          }
        }
        setNotScheduledModal(false);
        setPendingCustomer(null);
        onNavigate?.('Delivery RPM Submit', { customer, proceedWhenNotScheduled: true });
      } catch (err) {
        setNotScheduledModal(false);
        setPendingCustomer(null);
        setCustomerError(parseError(err));
      } finally {
        setProceedValidating(false);
      }
    } else {
      const pendingSscc = pendingSsccRef.current;
      const isAdd = pendingIsAddRef.current;
      setNotScheduledModal(false);
      pendingSsccRef.current = '';
      pendingIsAddRef.current = false;
      runValidation(pendingSscc, true, isAdd);
    }
  };

  // ── Customer handlers ──────────────────────────────────────────────────────

  const formatCustomerDisplay = (cof?: string, name?: string, fallback?: string) => {
    const cofText = (cof ?? '').trim();
    const nameText = (name ?? '').trim();
    if (cofText && nameText) return `${cofText} - ${nameText}`;
    if (cofText) return cofText;
    if (nameText) return nameText;
    return fallback ?? '';
  };

  const handleCustomerTextChange = (val: string) => {
    setCustomerText(val);
    setSelectedCustomer(null);
    setCustomerError('');

    if (!val.trim()) {
      setCustomerDropOpen(false);
      setCustomerOptions([]);
      setCustomerEmptyMsg(null);
      if (customerDebounceRef.current) clearTimeout(customerDebounceRef.current);
      return;
    }

    // Measure only on the closed→open transition; re-measuring every keystroke caused the mobile crash
    if (!customerDropOpen && Platform.OS !== 'web') measureCustomerDropdown();
    setCustomerDropOpen(true);
    if (customerDebounceRef.current) clearTimeout(customerDebounceRef.current);
    customerDebounceRef.current = setTimeout(async () => {
      setCustomerLoading(true);
      try {
      const res = await fetchRoadblockCustomers(val.trim(), currentSiteName, 25);
      setCustomerOptions(res.customers);
      // Prefer client-side localization for the empty message so it respects
      // the user's selected language. If the API provides a custom message
      // that must be shown, we would need the API to return a localized
      // message or a message key. For now use null so the UI falls back to
      // `t('one_way.empty_customers')` which updates with language changes.
      setCustomerEmptyMsg(null);
      } catch {
        setCustomerOptions([]);
        setCustomerEmptyMsg(t('one_way.error_load_customers'));
      } finally {
        setCustomerLoading(false);
      }
    }, 3000);
  };

  const handleSelectCustomer = (opt: CustomerOption) => {
    const cof = opt.customerCofId ?? '';
    const name = opt.customerName ?? opt.displayLabel;
    const optSscc = opt.sscc ?? opt.ssccList?.[0];
    setSelectedCustomer({
      cof,
      name,
      ...(optSscc ? { sscc: optSscc } : {}),
      ...(opt.ssccList?.length ? { ssccList: opt.ssccList } : {}),
    });
    setCustomerText(formatCustomerDisplay(cof, opt.customerName, opt.displayLabel));
    setCustomerDropOpen(false);
    setCustomerOptions([]);
    setCustomerError('');
  };

  const handleCustomerSubmit = async () => {
    if (!selectedCustomer) return;
    const customerSscc = selectedCustomer.sscc ?? selectedCustomer.ssccList?.[0];
    if (!customerSscc) {
      onNavigate?.('Delivery RPM Submit', { customer: selectedCustomer });
      return;
    }
    setCustomerValidating(true);
    setCustomerError('');
    try {
      const result = await validateHaulbackSscc({ sscc: customerSscc, proceedWhenNotScheduled: false });
      if (result.valid) {
        onNavigate?.('Delivery RPM Submit', { customer: selectedCustomer });
        return;
      }
      if (result.requiresProceedConfirmation && result.canProceedWithOverride) {
        setPendingCustomer(selectedCustomer);
        setNotScheduledModal(true);
        return;
      }
      setCustomerError(result.inlineError || result.bannerError || result.modalMessage || t('one_way.error_validate_sscc'));
    } catch (err) {
      setCustomerError(parseError(err));
    } finally {
      setCustomerValidating(false);
    }
  };

  // Clear all filters
  const clearAllFilters = () => {
    setColumnFilters({});
    setGlobalSearch('');
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    setDebouncedSearch('');
    setQuickFilter('all');
    setDateFrom(null);
    setDateTo(null);
    setSortBy(undefined);
    setSortDirection('asc');
    setCurrentPage(0);
  };

  // Quick-filter pill handler
  const handlePillPress = useCallback((key: QuickFilter) => {
    const next = quickFilter === key && key !== 'all' ? 'all' : key;
    setQuickFilter(next);
    setCurrentPage(0);

    if (next === 'all') {
      setDateFrom(null);
      setDateTo(null);
      setColumnFilters(prev => { const u = { ...prev }; delete u.status; return u; });
      return;
    }
    if (next === 'today') {
      const today = new Date();
      setDateFrom(today);
      setDateTo(today);
      setColumnFilters(prev => { const u = { ...prev }; delete u.status; return u; });
    } else if (next === 'last7') {
      const today = new Date();
      const sevenDaysAgo = new Date(Date.now() - 6 * 86400000);
      setDateFrom(sevenDaysAgo);
      setDateTo(today);
      setColumnFilters(prev => { const u = { ...prev }; delete u.status; return u; });
    } else if (next === 'rescheduled') {
      setDateFrom(null);
      setDateTo(null);
      setColumnFilters(prev => ({ ...prev, status: ['Rescheduled'] }));
    } else if (next === 'return-to-stock') {
      setDateFrom(null);
      setDateTo(null);
      setColumnFilters(prev => ({ ...prev, status: ['Return To Stock'] }));
    }
  }, [quickFilter]);

  const filteredData = allData;
  const totalPages = Math.max(1, Math.ceil(totalElements / pageSize));
  const pageData = filteredData;

  const hasActiveFilters = Object.values(columnFilters).some(v => v.length > 0) || !!globalSearch.trim() || quickFilter !== 'all' || !!dateFrom || !!dateTo;

  const reasonCodeFilterOptions = useMemo(() =>
    Object.values(haulbackRcMap)
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b))
      .map(label => ({ label, value: label })),
  [haulbackRcMap]);

  // Quick filter pills — built from t() so they re-render on language change
  const pillConfig = useMemo(() => [
    { key: 'all' as QuickFilter,            labelKey: 'one_way.quick_filters.all' },
    { key: 'today' as QuickFilter,          labelKey: 'one_way.quick_filters.today' },
    { key: 'last7' as QuickFilter,          labelKey: 'one_way.quick_filters.last7' },
    { key: 'rescheduled' as QuickFilter,    labelKey: 'one_way.quick_filters.rescheduled' },
    { key: 'return-to-stock' as QuickFilter, labelKey: 'one_way.quick_filters.return_to_stock' },
  ], []);

  // Columns definition
  const columns = useMemo(() => [
    {
      columnKey: 'customer',
      header: t('one_way.table_headers.customer'),
      flexGrow: 2,
      sortable: true,
      renderCell: (item: HaulbackRecord) => (
        <View>
          <Text style={{ fontSize: 14, fontWeight: '600', color: dc('#1A1A1A'), fontFamily: FontFamily.bold }} numberOfLines={1}>{item.customer}</Text>
          <Text style={{ fontSize: 12, color: dc('#9E9E9E'), fontFamily: FontFamily.regular, letterSpacing: 0.3 }}>{item.cof}</Text>
        </View>
      ),
    },
    {
      columnKey: 'sscc',
      header: t('one_way.table_headers.sscc'),
      flexGrow: 2,
      sortable: true,
      formatter: (val: string) => val || '—',
    },
    {
      columnKey: 'localRoute',
      header: t('one_way.table_headers.local_route'),
      flexGrow: 1,
      sortable: true,
    },
    {
      columnKey: 'date',
      header: t('one_way.table_headers.date'),
      flexGrow: 1,
      sortable: true,
      formatter: (val: string) => formatDate(val),
      dateRangeFilter: {
        fromDate: dateFrom,
        toDate: dateTo,
        onFromChange: (d: Date | null) => { setDateFrom(d); setCurrentPage(0); },
        onToChange: (d: Date | null) => { setDateTo(d); setCurrentPage(0); },
        onClear: () => { setDateFrom(null); setDateTo(null); setCurrentPage(0); },
      },
    },
    {
      columnKey: 'status',
      header: t('one_way.table_headers.status'),
      flexGrow: 1.5,
      sortable: true,
      filterOptions: [
        { label: t('one_way.status_badges.rescheduled'), value: 'Rescheduled' },
        { label: t('one_way.status_badges.return_to_stock'), value: 'Return to Stock' },
      ],
      renderCell: (item: HaulbackRecord) => <StatusBadge status={item.status} t={t} />,
    },
    {
      columnKey: 'reasonCode',
      header: t('one_way.table_headers.reason_code'),
      flexGrow: 2,
      sortable: true,
      filterOptions: reasonCodeFilterOptions,
    },
    {
      columnKey: 'caseQuantity',
      header: t('one_way.table_headers.case_quantity'),
      flexGrow: 1,
      sortable: true,
      formatter: (val: number) => String(val),
    },
    {
      columnKey: 'driver',
      header: t('one_way.table_headers.driver'),
      flexGrow: 1,
      sortable: true,
    },
    {
      columnKey: 'salesRep',
      header: t('one_way.table_headers.sales_rep'),
      flexGrow: 1,
      sortable: true,
    },
    {
      columnKey: 'picker',
      header: t('one_way.table_headers.picker'),
      flexGrow: 1,
      sortable: true,
    },
  ], [dateFrom, dateTo, reasonCodeFilterOptions, t]);

  const handleSort = useCallback((columnKey: string, direction: 'asc' | 'desc') => {
    const backendKey = SORT_KEY_MAP[columnKey];
    if (!backendKey) return;
    setSortBy(backendKey);
    setSortDirection(direction);
    setCurrentPage(0);
  }, []);

  const handleColumnFilter = useCallback((columnKey: string, values: string[]) => {
    setColumnFilters(prev => ({ ...prev, [columnKey]: values }));
    setCurrentPage(0);
  }, []);

  const ssccReady = sscc.length === SSCC_MAX_LEN;
  const canProceedWithStagedSsccs = stagedSsccs.length > 0 && sscc.length === 0;
  const scanDisabled = Platform.OS === 'web';

  const handleOpenRecord = useCallback((item: HaulbackRecord) => {
    setSelectedRecordId(item.id);
  }, []);

  const handleRecordGone = useCallback(() => {
    setSelectedRecordId(null);
    showToast({
      header: '',
      subtext: t('one_way.record_gone_message'),
      variant: 'info',
      position: 'bottom',
      duration: 4000,
    });
    loadSubmissions();
  }, [loadSubmissions, showToast, t]);

  const renderMobileCard = ({ item }: { item: HaulbackRecord }) => (
    <Pressable style={mob.card} onPress={() => handleOpenRecord(item)}>
      <View style={mob.top}>
        <Text style={mob.customer} numberOfLines={1}>{item.customer}</Text>
        <StatusBadge status={item.status} t={t} />
      </View>
      <View style={mob.row}>
        <Text style={mob.meta}>{t('one_way.table_headers.local_route')} <Text style={mob.val}>{item.localRoute}</Text></Text>
        <Text style={mob.meta}>{t('one_way.table_headers.case_quantity')} <Text style={mob.val}>{item.caseQuantity}</Text></Text>
        <Text style={mob.meta}><Text style={mob.val}>{formatDate(item.date)}</Text></Text>
      </View>
      <View style={mob.row}>
        <Text style={mob.meta}>{t('one_way.mobile_label_reason')} <Text style={mob.val}>{item.reasonCode}</Text></Text>
      </View>
    </Pressable>
  );

  return (
    <View style={s.root}>
      <ScrollView
        style={s.scroll}
        contentContainerStyle={s.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        {!isFullAccess && (
          <View style={s.readNote}>
            <EyeIcon />
            <Text style={s.readNoteText}>
              {t('one_way.readonly_banner')}
            </Text>
          </View>
        )}

        {/* ── Entry card (full-access only) ────────────────────────────── */}
        {isFullAccess && (
          <View style={[s.ssccCard, Platform.OS === 'web' ? { overflow: 'visible' } as any : {}]}>
            {/* Card header row: title/subtitle + SSCC/Customer toggle */}
            <View style={s.ssccCardHeadRow}>
              <View style={{ flex: 1 }}>
                <Text style={s.ssccTitle}>{t('one_way.card_entry_title')}</Text>
                <Text style={s.ssccSubtitle}>
                  {entryMode === 'sscc' ? t('one_way.card_sscc_sub') : t('one_way.card_customer_sub')}
                </Text>
              </View>
              {/* Entry mode toggle */}
              <View style={[s.switcher, isDarkTheme && { backgroundColor: dc('#000000', 'bg'), borderWidth: 1, borderColor: dc('#3A3A3A', 'border') }]}>
                <Pressable
                  style={[s.swOpt, entryMode === 'sscc' && s.swOptActive, isDarkTheme && entryMode === 'sscc' && { backgroundColor: '#3D8BFF' }]}
                  onPress={() => { setEntryMode('sscc'); setCustomerError(''); setSsccError(''); }}
                >
                  <Text style={[s.swOptText, entryMode === 'sscc' && s.swOptTextActive, isDarkTheme && entryMode === 'sscc' && { color: '#FFFFFF' }]}>
                    {t('one_way.switcher_by_sscc')}
                  </Text>
                </Pressable>
                <Pressable
                  style={[s.swOpt, entryMode === 'customer' && s.swOptActive, isDarkTheme && entryMode === 'customer' && { backgroundColor: '#3D8BFF' }]}
                  onPress={() => { setEntryMode('customer'); setSsccError(''); setCustomerError(''); }}
                >
                  <Text style={[s.swOptText, entryMode === 'customer' && s.swOptTextActive, isDarkTheme && entryMode === 'customer' && { color: '#FFFFFF' }]}>
                    {t('one_way.switcher_by_customer')}
                  </Text>
                </Pressable>
              </View>
            </View>

            {/* ── SSCC mode ─────────────────────────────────────── */}
            {entryMode === 'sscc' && (
              <View style={s.ssccRow}>
                {/* Input box */}
                <View style={s.ssccBox}>
                  <Text style={s.ssccFieldLabel}>
                    {t('one_way.label_enter_sscc')}<Text style={s.ssccRequired}> *</Text>
                  </Text>
                  <View style={s.ssccWrap}>
                    <TextInput
                      ref={ssccInputRef}
                      style={s.ssccInput}
                      value={sscc}
                      onChangeText={handleSsccChange}
                      onBlur={handleSsccBlur}
                      placeholder={t('one_way.placeholder_sscc')}
                      placeholderTextColor={dc("#B1B1B1")}
                      keyboardType="numeric"
                      maxLength={SSCC_MAX_LEN}
                      returnKeyType="go"
                      onSubmitEditing={handleSsccSubmit}
                      accessibilityLabel={t('one_way.label_enter_sscc')}
                    />

                    {/* + Add-to-list button */}
                    <Pressable
                      style={[s.addBtn, ssccReady && !validating && s.addBtnReady]}
                      onPress={ssccReady && !validating ? handleAddToList : undefined}
                      disabled={!ssccReady || validating}
                      accessibilityLabel={t('one_way.btn_add_sscc_tooltip')}
                      accessibilityHint={t('one_way.btn_add_sscc_tooltip')}
                      {...(Platform.OS === 'web'
                        ? {
                            onMouseEnter: (e: any) => {
                              const x = e?.clientX ?? e?.nativeEvent?.clientX ?? 0;
                              const y = e?.clientY ?? e?.nativeEvent?.clientY ?? 0;
                              setPlusTooltipPosition({ x, y });
                            },
                            onMouseMove: (e: any) => {
                              const x = e?.clientX ?? e?.nativeEvent?.clientX ?? 0;
                              const y = e?.clientY ?? e?.nativeEvent?.clientY ?? 0;
                              setPlusTooltipPosition(prev => (prev ? { x, y } : null));
                            },
                            onMouseLeave: () => setPlusTooltipPosition(null),
                          } as any
                        : {})}
                    >
                      <PlusIcon ready={ssccReady && !validating} />
                    </Pressable>

                    {/* → Submit / proceed arrow */}
                    <Pressable
                      style={[s.goArrow, (ssccReady || canProceedWithStagedSsccs) && !validating && s.goArrowReady]}
                      onPress={(ssccReady || canProceedWithStagedSsccs) && !validating ? handleSsccSubmit : undefined}
                      disabled={!(ssccReady || canProceedWithStagedSsccs) || validating}
                      accessibilityLabel={t('one_way.label_enter_sscc')}
                    >
                      {validating
                        ? <ActivityIndicator size="small" color="#fff" />
                        : <GoArrowIcon ready={ssccReady || canProceedWithStagedSsccs} />}
                    </Pressable>
                  </View>
                  <View style={s.ssccMeta}>
                    {!!ssccError
                      ? <Text style={s.ssccErr}>{ssccError}</Text>
                      : <View />}
                    <Text style={[s.counter, sscc.length === SSCC_MAX_LEN && s.counterDone]}>
                      {sscc.length}/{SSCC_MAX_LEN}
                    </Text>
                  </View>

                  {/* Staged SSCCs chips */}
                  {stagedSsccs.length > 0 && (
                    <View style={s.ssccChipList}>
                      {stagedSsccs.map(item => (
                        <View key={item.id} style={s.ssccChip}>
                          <Text style={s.ssccChipText}>{item.sscc}</Text>
                          <Pressable
                            style={s.ssccChipRemove}
                            onPress={() => handleRemoveStagedSscc(item.id)}
                            accessibilityLabel={`Remove SSCC ${item.sscc}`}
                          >
                            <MaterialIcons name="close" size={11} color="#fff" />
                          </Pressable>
                        </View>
                      ))}
                    </View>
                  )}
                </View>

                {/* Scan button column */}
                <View style={s.scanCol}>
                  <Pressable
                    style={[s.scanBtn, scanDisabled && s.scanBtnDisabled]}
                    onPress={scanDisabled ? undefined : handleScan}
                    disabled={scanDisabled}
                    accessibilityLabel={scanDisabled ? t('one_way.scan_help_text') : t('one_way.scan_btn')}
                  >
                    <ScanIcon color={scanDisabled ? '#BDBDBD' : '#005CBC'} />
                    <Text style={[s.scanBtnText, scanDisabled && s.scanBtnTextDisabled]}>
                      {t('one_way.scan_btn')}
                    </Text>
                  </Pressable>
                  {scanDisabled && (
                    <Text style={s.scanHint}>{t('one_way.scan_help_text')}</Text>
                  )}
                  {cameraDenied && !scanDisabled && (
                    <Text style={s.scanHint}>{t('one_way.scan_camera_denied')}</Text>
                  )}
                </View>
              </View>
            )}

            {/* ── Customer mode ─────────────────────────────────── */}
            {entryMode === 'customer' && (
              <View style={s.ssccBox}>
                <Text style={s.ssccFieldLabel}>{t('one_way.switcher_by_customer')}</Text>
                <View style={s.taWrap}>
                  <View style={s.ssccWrap} ref={customerInputWrapRef}>
                    <TextInput
                      style={s.ssccInput}
                      value={customerText}
                      onChangeText={handleCustomerTextChange}
                      placeholder={t('one_way.placeholder_customer_search')}
                      placeholderTextColor="#B1B1B1"
                      onFocus={() => {
                        if (customerText.trim() && !selectedCustomer && !customerDropOpen) {
                          if (Platform.OS !== 'web') measureCustomerDropdown();
                          setCustomerDropOpen(true);
                        }
                      }}
                      onBlur={() => {
                        if (customerDebounceRef.current) clearTimeout(customerDebounceRef.current);
                        setTimeout(() => {
                          if (!customerSelectingRef.current) setCustomerDropOpen(false);
                          customerSelectingRef.current = false;
                        }, 200);
                      }}
                    />
                    {customerValidating
                      ? <ActivityIndicator size="small" color={dc("#005CBC")} style={{ marginLeft: 8 }} />
                      : (
                        <Pressable
                          style={[s.goArrow, selectedCustomer !== null && s.goArrowReady]}
                          onPress={selectedCustomer !== null && !customerValidating ? handleCustomerSubmit : undefined}
                          disabled={selectedCustomer === null || customerValidating}
                          accessibilityLabel={t('one_way.switcher_by_customer')}
                        >
                          <GoArrowIcon ready={selectedCustomer !== null} />
                        </Pressable>
                      )
                    }
                  </View>
                  {!!customerError && (
                    <Text style={s.ssccErr}>{customerError}</Text>
                  )}
                  {/* Web: Absolute positioned dropdown */}
                  {Platform.OS === 'web' && customerDropOpen && (
                    <View style={s.taDropdown}>
                      {customerLoading
                        ? <ActivityIndicator size="small" color={dc("#005CBC")} style={{ padding: 8 }} />
                        : customerOptions.length === 0
                          ? <Text style={s.taEmpty}>{customerEmptyMsg ?? t('one_way.empty_customers')}</Text>
                          : (
                            <ScrollView
                              style={s.taList}
                              keyboardShouldPersistTaps="handled"
                              nestedScrollEnabled
                              showsVerticalScrollIndicator
                            >
                              {customerOptions.map((opt, idx) => {
                                const label = formatCustomerDisplay(opt.customerCofId, opt.customerName, opt.displayLabel);
                                return (
                                  <Pressable
                                    key={opt.customerCofId ?? idx}
                                    style={s.taOption}
                                    onPressIn={() => { customerSelectingRef.current = true; }}
                                    onPress={() => handleSelectCustomer(opt)}
                                  >
                                    {opt.customerCofId ? (
                                      <Text style={s.taCofCol} numberOfLines={1}>{opt.customerCofId}</Text>
                                    ) : null}
                                    {opt.customerCofId ? (
                                      <Text style={s.taSep}>-</Text>
                                    ) : null}
                                    <Text style={s.taName} numberOfLines={1}>{opt.customerName ?? label}</Text>
                                  </Pressable>
                                );
                              })}
                            </ScrollView>
                          )
                      }
                    </View>
                  )}

                  {/* Mobile: Modal overlay keeps the dropdown above the ScrollView's clipping bounds (same pattern as DateRangeFilterDropdown) */}
                  {Platform.OS !== 'web' && customerDropOpen && (
                    <Modal
                      visible={customerDropOpen}
                      transparent
                      animationType="none"
                      onRequestClose={() => setCustomerDropOpen(false)}
                    >
                      <TouchableWithoutFeedback onPress={() => setCustomerDropOpen(false)}>
                        <View style={{ flex: 1 }}>
                          <TouchableWithoutFeedback onPress={() => { /* stop propagation */ }}>
                            <View
                              style={[
                                s.taDropdown,
                                {
                                  position: 'absolute',
                                  top: customerDropPos.top,
                                  left: customerDropPos.left,
                                  width: customerDropPos.width,
                                  maxHeight: 300,
                                },
                              ]}
                            >
                              {customerLoading
                                ? <ActivityIndicator size="small" color="#005CBC" style={{ padding: 8 }} />
                                : customerOptions.length === 0
                                  ? <Text style={s.taEmpty}>{customerEmptyMsg ?? t('one_way.empty_customers')}</Text>
                                  : (
                                    <ScrollView
                                      style={[s.taList, { maxHeight: 300 }]}
                                      keyboardShouldPersistTaps="handled"
                                      nestedScrollEnabled
                                      showsVerticalScrollIndicator
                                    >
                                      {customerOptions.map((opt, idx) => {
                                        const label = formatCustomerDisplay(opt.customerCofId, opt.customerName, opt.displayLabel);
                                        return (
                                          <Pressable
                                            key={opt.customerCofId ?? idx}
                                            style={s.taOption}
                                            onPress={() => {
                                              handleSelectCustomer(opt);
                                              setCustomerDropOpen(false);
                                            }}
                                          >
                                            {opt.customerCofId ? (
                                              <Text style={s.taCofCol} numberOfLines={1}>{opt.customerCofId}</Text>
                                            ) : null}
                                            {opt.customerCofId ? (
                                              <Text style={s.taSep}>-</Text>
                                            ) : null}
                                            <Text style={s.taName} numberOfLines={1}>{opt.customerName ?? label}</Text>
                                          </Pressable>
                                        );
                                      })}
                                    </ScrollView>
                                  )
                              }
                            </View>
                          </TouchableWithoutFeedback>
                        </View>
                      </TouchableWithoutFeedback>
                    </Modal>
                  )}
                </View>
              </View>
            )}
          </View>
        )}

        {/* ── Table card ──────────────────────────────────────────────── */}
        <View style={s.tableCard}>
          {/* Quick filter pills */}
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            style={s.pillsScroll}
            contentContainerStyle={s.pills}
          >
            {pillConfig.map(({ key, labelKey }) => (
              <Pressable
                key={key}
                style={[s.pill, quickFilter === key && s.pillActive]}
                onPress={() => handlePillPress(key)}
              >
                <Text style={[s.pillText, quickFilter === key && s.pillTextActive]}>{t(labelKey)}</Text>
              </Pressable>
            ))}
          </ScrollView>

          {/* Table top row */}
          <View style={s.tableTop}>
            <Text style={s.tableSummary}>
              {t('one_way.table_summary', { shown: pageData.length, total: totalElements })}
            </Text>
            <View style={s.tableTools}>
              <View style={s.searchWrap}>
                <MaterialIcons name="search" size={16} color={dc("#838383")} />
                <TextInput
                  style={s.searchInput}
                  value={globalSearch}
                  onChangeText={v => {
                    setGlobalSearch(v);
                    setCurrentPage(0);
                    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
                    searchDebounceRef.current = setTimeout(() => setDebouncedSearch(v), 400);
                  }}
                  placeholder={t('one_way.search_placeholder')}
                  placeholderTextColor={dc("#838383")}
                />
              </View>
              {hasActiveFilters && (
                <Pressable style={s.clearAllBtn} onPress={clearAllFilters}>
                  <MaterialIcons name="close" size={14} color={dc("#005CBC")} style={{ marginRight: 4 }} />
                  <Text style={s.clearAllText}>{t('one_way.btn_clear_filters')}</Text>
                </Pressable>
              )}
            </View>
          </View>

          {/* Desktop table */}
          {!isMobile && (loadingData || pageData.length > 0) && (
            <HarborDataTable
              data={pageData}
              columns={columns}
              tableHeader={t('one_way.table_header')}
              tableDescription={t('one_way.table_summary', { shown: pageData.length, total: totalElements })}
              showHeaderTopBorder={false}
              onRowClick={(row) => handleOpenRecord(row as HaulbackRecord)}
              isLoading={loadingData}
              maxRecordsToShow={pageSize}
              externalPage={currentPage}
              externalTotalPages={totalPages}
              onExternalPageChange={(newPage) => setCurrentPage(newPage)}
              onPageSizeChange={(newSize) => setPageSize(newSize)}
              onSort={handleSort}
              isFirstPage={currentPage === 0}
              isLastPage={currentPage === totalPages - 1}
              columnFilters={columnFilters}
              onColumnFilter={handleColumnFilter}
              locale={{
                rowsPerPageLabel: t('delivery_rpm.pagination.rows_per_page'),
                pageLabel: t('delivery_rpm.pagination.page_label'),
                pageOfLabel: t('delivery_rpm.pagination.page_of_label'),
                firstPageLabel: t('delivery_rpm.pagination.first_page_label'),
                prevPageLabel: t('delivery_rpm.pagination.prev_page_label'),
                nextPageLabel: t('delivery_rpm.pagination.next_page_label'),
                lastPageLabel: t('delivery_rpm.pagination.last_page_label'),
                noDataLabel: t('delivery_rpm.pagination.no_data_label'),
              }}
            />
          )}

          {/* Empty state */}
          {!loadingData && pageData.length === 0 && (
            <EmptyState
              hasFilters={hasActiveFilters}
              onClear={clearAllFilters}
              onSubmitFirst={!isMobile && isFullAccess ? () => ssccInputRef.current?.focus() : undefined}
              totalRecords={totalElements}
              t={t}
            />
          )}

          {/* Mobile card list */}
          {isMobile && !loadingData && pageData.length > 0 && (
            <>
            <View style={s.mobileList}>
              <FlatList
                data={pageData}
                keyExtractor={item => item.id}
                renderItem={renderMobileCard}
                scrollEnabled={false}
                ItemSeparatorComponent={() => <View style={{ height: 10 }} />}
              />
            </View>
            {totalPages > 1 && (
              <View style={s.pagFoot}>
                <TouchableOpacity
                  style={[s.pgBtn, currentPage === 0 && s.pgBtnDisabled]}
                  onPress={() => setCurrentPage(p => Math.max(0, p - 1))}
                  disabled={currentPage === 0}
                >
                  <MaterialIcons name="chevron-left" size={16} color={currentPage === 0 ? '#BDBDBD' : '#616161'} />
                </TouchableOpacity>
                {getPageNumbers(currentPage, totalPages).map((page, idx) =>
                  page === '...'
                    ? <Text key={`ell-${idx}`} style={[s.pgBtnText, { paddingHorizontal: 2 }]}>…</Text>
                    : <TouchableOpacity
                        key={page}
                        style={[s.pgBtn, (page as number) === currentPage && s.pgBtnActive]}
                        onPress={() => setCurrentPage(page as number)}
                      >
                        <Text style={[s.pgBtnText, (page as number) === currentPage && s.pgBtnActiveText]}>
                          {(page as number) + 1}
                        </Text>
                      </TouchableOpacity>
                )}
                <TouchableOpacity
                  style={[s.pgBtn, currentPage === totalPages - 1 && s.pgBtnDisabled]}
                  onPress={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
                  disabled={currentPage === totalPages - 1}
                >
                  <MaterialIcons name="chevron-right" size={16} color={currentPage === totalPages - 1 ? '#BDBDBD' : '#616161'} />
                </TouchableOpacity>
              </View>
            )}
            </>
          )}
        </View>
      </ScrollView>

      {/* ── Record detail modal ──────────────────────────────────────────── */}
      <HaulbackRecordModal
        recordId={selectedRecordId}
        onClose={() => setSelectedRecordId(null)}
        onRecordGone={handleRecordGone}
      />

      {/* ── "Not scheduled today" modal ──────────────────────────────────── */}
      <Modal
        visible={notScheduledModal}
        transparent
        animationType="fade"
        onRequestClose={handleNotScheduledBack}
      >
        <View style={{
          flex: 1, backgroundColor: 'rgba(0,0,0,0.5)',
          alignItems: 'center', justifyContent: 'center', padding: 20,
        }}>
          <View style={{
            backgroundColor: dc('#fff', 'bg'), borderRadius: 8, width: '100%', maxWidth: 430,
            overflow: 'hidden',
            ...(Platform.OS === 'ios' ? { shadowColor: '#005EBC', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.12, shadowRadius: 24 } : { elevation: 8 }),
          }}>
            {/* Modal header */}
            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: 16, paddingHorizontal: 24, borderBottomWidth: 1, borderBottomColor: dc('#EBEBEB', 'border') }}>
              <Text style={{ fontSize: 18, fontWeight: '700', color: dc('#1A1A1A'), fontFamily: FontFamily.bold }}>
                {t('one_way.error_not_scheduled_modal_title')}
              </Text>
              <TouchableOpacity
                style={{ width: 30, height: 30, borderRadius: 6, alignItems: 'center', justifyContent: 'center' }}
                onPress={handleNotScheduledBack}
                accessibilityLabel={t('common.close')}
              >
                <MaterialIcons name="close" size={16} color={dc("#616161")} />
              </TouchableOpacity>
            </View>

            {/* Modal body */}
            <View style={{ padding: 24, gap: 14 }}>
              <View style={{
                flexDirection: 'row', alignItems: 'flex-start', gap: 10,
                backgroundColor: isDarkTheme ? '#92600A' : '#FEF1B9', borderWidth: 1, borderColor: '#E5A408',
                borderRadius: 8, padding: 12,
              }}>
                <MaterialIcons name="warning" size={20} color="#E5A408" style={{ marginTop: 1 }} />
                <Text style={{ flex: 1, fontSize: 14, lineHeight: 20, color: '#6b4a05', fontFamily: FontFamily.regular }}>
                  {t('one_way.error_not_scheduled_warning')}
                </Text>
              </View>
              <Text style={{ fontSize: 12, color: dc('#838383'), fontFamily: FontFamily.regular, lineHeight: 18 }}>
                {t('one_way.error_not_scheduled_fine')}
              </Text>
            </View>

            {/* Modal footer */}
            <View style={{ flexDirection: 'row', justifyContent: 'flex-end', gap: 16, padding: 16, paddingHorizontal: 24, borderTopWidth: 1, borderTopColor: dc('#EBEBEB', 'border') }}>
              <TouchableOpacity
                style={{ height: 40, paddingHorizontal: 24, borderRadius: 24, borderWidth: 1, borderColor: dc('#034895', 'border'), alignItems: 'center', justifyContent: 'center' }}
                onPress={handleNotScheduledBack}
                accessibilityLabel={t('one_way.modal_action_back')}
              >
                <Text style={{ fontSize: 16, fontWeight: '700', color: dc('#034895'), fontFamily: FontFamily.bold }}>{t('one_way.modal_action_back')}</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={{ height: 40, paddingHorizontal: 24, borderRadius: 24, backgroundColor: dc('#034895', 'bg'), alignItems: 'center', justifyContent: 'center' }}
                onPress={proceedValidating ? undefined : handleNotScheduledProceed}
                disabled={proceedValidating}
                accessibilityLabel={t('one_way.modal_action_proceed')}
              >
                {proceedValidating
                  ? <ActivityIndicator size="small" color="#fff" />
                  : <Text style={{ fontSize: 16, fontWeight: '700', color: '#fff', fontFamily: FontFamily.bold }}>{t('one_way.modal_action_proceed')}</Text>
                }
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* ── Same-customer error modal ─────────────────────────────────────── */}
      <Modal
        visible={sameCustomerModal}
        transparent
        animationType="fade"
        onRequestClose={() => setSameCustomerModal(false)}
      >
        <View style={{
          flex: 1, backgroundColor: 'rgba(0,0,0,0.5)',
          alignItems: 'center', justifyContent: 'center', padding: 20,
        }}>
          <View style={{
            backgroundColor: dc('#fff', 'bg'), borderRadius: 8, width: '100%', maxWidth: 380,
            overflow: 'hidden',
            ...(Platform.OS === 'ios' ? { shadowColor: '#005EBC', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.12, shadowRadius: 24 } : { elevation: 8 }),
          }}>
            <View style={{ padding: 24, gap: 16, alignItems: 'center' }}>
              <MaterialIcons name="error-outline" size={36} color="#C12C01" />
              <Text style={{ fontSize: 16, fontWeight: '700', color: dc('#1A1A1A'), fontFamily: FontFamily.bold, textAlign: 'center' }}>
                {t('one_way.error_sscc_different_customer')}
              </Text>
              <TouchableOpacity
                style={{ height: 40, paddingHorizontal: 32, borderRadius: 24, backgroundColor: dc('#034895', 'bg'), alignItems: 'center', justifyContent: 'center', marginTop: 4 }}
                onPress={() => setSameCustomerModal(false)}
                accessibilityLabel={t('one_way.sscc_list_modal_ok')}
              >
                <Text style={{ fontSize: 16, fontWeight: '700', color: '#fff', fontFamily: FontFamily.bold }}>
                  {t('one_way.sscc_list_modal_ok')}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* ── Barcode scanner modal ────────────────────────────────────────── */}
      <BarcodeScannerSimple
        visible={barcodeScannerVisible}
        onClose={() => setBarcodeScannerVisible(false)}
        onBarcodeScanned={handleBarcodeScanSuccess}
        onPermissionDenied={() => {
          setBarcodeScannerVisible(false);
          setCameraDenied(true);
        }}
        expectedLength={SSCC_MAX_LEN}
        title={t('one_way.scan_title')}
      />

      <PlusTooltip
        visible={plusTooltipPosition !== null}
        position={plusTooltipPosition}
        text={t('one_way.btn_add_sscc_tooltip')}
      />
    </View>
  );
};

// ─── Empty state ─────────────────────────────────────────────────────────────

const baseNotScheduledMs = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  sheet: {
    backgroundColor: '#fff',
    borderRadius: 8,
    width: '100%',
    maxWidth: 430,
    overflow: 'hidden',
    ...Platform.select({
      ios: { shadowColor: '#005EBC', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.12, shadowRadius: 24 },
      android: { elevation: 8 },
    }),
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    paddingHorizontal: 24,
    borderBottomWidth: 1,
    borderBottomColor: '#EBEBEB',
  },
  title: { fontSize: 18, fontWeight: '700', color: '#1A1A1A', fontFamily: FontFamily.bold },
  closeBtn: {
    width: 30,
    height: 30,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
  },
  body: { padding: 24, gap: 14 },
  warnBanner: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
    backgroundColor: '#FEF1B9',
    borderWidth: 1,
    borderColor: '#E5A408',
    borderRadius: 8,
    padding: 12,
  },
  warnText: { flex: 1, fontSize: 14, lineHeight: 20, color: '#6b4a05', fontFamily: FontFamily.regular },
  fineText: { fontSize: 12, color: '#838383', fontFamily: FontFamily.regular, lineHeight: 18 },
  footer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 16,
    padding: 16,
    paddingHorizontal: 24,
    borderTopWidth: 1,
    borderTopColor: '#EBEBEB',
  },
  btnBack: {
    height: 40,
    paddingHorizontal: 24,
    borderRadius: 24,
    borderWidth: 1,
    borderColor: '#034895',
    alignItems: 'center',
    justifyContent: 'center',
  },
  btnBackText: { fontSize: 16, fontWeight: '700', color: '#034895', fontFamily: FontFamily.bold },
  btnProceed: {
    height: 40,
    paddingHorizontal: 24,
    borderRadius: 24,
    backgroundColor: '#034895',
    alignItems: 'center',
    justifyContent: 'center',
  },
  btnProceedText: { fontSize: 16, fontWeight: '700', color: '#fff', fontFamily: FontFamily.bold },
});

// ─── Not Scheduled Modal Component ───────────────────────────────────────────

interface NotScheduledModalProps {
  onClose: () => void;
  onProceed: () => void;
  isValidating: boolean;
  t: (key: string) => string;
}

const NotScheduledModal: React.FC<NotScheduledModalProps> = ({ onClose, onProceed, isValidating, t }) => {
  const ms = useDarkStyles(baseNotScheduledMs);
  // The modal is portaled outside the screen tree and its white sheet is not reliably reached by
  // the global dark-mode sweep, so the surface/dividers are set explicitly for dark mode.
  const isDark = useTheme().resolvedTheme === 'dark';

  return (
    <View style={ms.overlay}>
      <View style={[ms.sheet, isDark && { backgroundColor: '#202020' }]}>
        {/* Modal header */}
        <View style={[ms.header, isDark && { borderBottomColor: '#363636' }]}>
          <Text style={ms.title}>{t('one_way.error_not_scheduled_modal_title')}</Text>
          <TouchableOpacity
            style={ms.closeBtn}
            onPress={onClose}
            accessibilityLabel={t('common.close')}
          >
            <MaterialIcons name="close" size={16} color={isDark ? '#FFFFFF' : '#616161'} />
          </TouchableOpacity>
        </View>

        {/* Modal body */}
        <View style={ms.body}>
          <View style={ms.warnBanner}>
            <MaterialIcons name="warning" size={20} color="#E5A408" style={{ marginTop: 1 }} />
            <Text style={ms.warnText}>{t('one_way.error_not_scheduled_warning')}</Text>
          </View>
          <Text style={ms.fineText}>{t('one_way.error_not_scheduled_fine')}</Text>
        </View>

        {/* Modal footer */}
        <View style={[ms.footer, isDark && { borderTopColor: '#363636' }]}>
          <TouchableOpacity
            style={[ms.btnBack, isDark && { borderColor: '#3D8BFF' }]}
            onPress={onClose}
            accessibilityLabel={t('one_way.modal_action_back')}
          >
            <Text style={ms.btnBackText}>{t('one_way.modal_action_back')}</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={ms.btnProceed}
            onPress={isValidating ? undefined : onProceed}
            disabled={isValidating}
            accessibilityLabel={t('one_way.modal_action_proceed')}
          >
            {isValidating
              ? <ActivityIndicator size="small" color="#fff" />
              : <Text style={ms.btnProceedText}>{t('one_way.modal_action_proceed')}</Text>}
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
};

// ─── Empty state ─────────────────────────────────────────────────────────────

const EmptyState = ({
  hasFilters,
  onClear,
  onSubmitFirst,
  totalRecords,
  t,
}: {
  hasFilters: boolean;
  onClear: () => void;
  onSubmitFirst?: () => void;
  totalRecords: number;
  t: (key: string) => string;
}) => {
  const dc = useDarkColor();
  const es = useDarkStyles(baseEs);
  const { t: tGlobal } = useTranslation('harbor');
  return (
    <View style={es.wrap}>
      <MaterialIcons name="inbox" size={34} color={dc("#E0E0E0")} />
      <Text style={es.title}>
        {tGlobal('one_way.empty_state_title')}
      </Text>
      {hasFilters ? (
        <Pressable style={es.btn} onPress={onClear}>
          <Text style={es.btnText}>{tGlobal('one_way.btn_clear_filters')}</Text>
        </Pressable>
      ) : onSubmitFirst ? (
        <Pressable style={es.btn} onPress={onSubmitFirst}>
          <Text style={es.btnText}>{tGlobal('one_way.btn_submit_haulback')}</Text>
        </Pressable>
      ) : null}
    </View>
  );
};

export default OneWayScreen;
